import React, { useEffect, useMemo, useState } from "react";
import { Search, Truck, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

// Checkbox list of agency groups for the delivery_man role.
export function DeliveryAgencyPicker({ value, onChange }) {
  const [groups, setGroups] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => {
    const isIndane = (n) => /INDANE|IOCL|INDIAN OIL|IOC/i.test(n || "");
    api.get("/groups").then(({ data }) => setGroups((data || []).filter((g) => isIndane(g.name)).sort((a, b) => a.name.localeCompare(b.name)))).catch(() => {});
  }, []);
  const shown = useMemo(() => groups.filter((g) => g.name.toLowerCase().includes(q.toLowerCase())), [groups, q]);
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <div className="border border-[#DCDCD3] rounded-md" data-testid="delivery-agency-picker">
      <div className="flex items-center gap-2 px-2 py-1.5 border-b border-[#DCDCD3]">
        <Search className="w-3.5 h-3.5 text-[#6B6B61]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search INDANE agency…"
          className="flex-1 text-sm outline-none" data-testid="delivery-agency-search" />
        <span className="text-[11px] font-mono text-[#385C4B]" data-testid="delivery-agency-selected-count">{value.length} selected</span>
      </div>
      <div className="max-h-52 overflow-y-auto p-1">
        {shown.map((g) => (
          <label key={g.id} className="flex items-center gap-2 px-2 py-1 text-sm rounded hover:bg-[#F5F5F0] cursor-pointer">
            <input type="checkbox" checked={value.includes(g.id)} onChange={() => toggle(g.id)}
              className="accent-[#385C4B]" data-testid={`delivery-agency-cb-${g.id}`} />
            {g.name}
          </label>
        ))}
        {shown.length === 0 && <div className="text-xs text-[#6B6B61] p-2">Koi agency nahi mili</div>}
      </div>
    </div>
  );
}

export function DeliveryAgenciesButton({ user, onSaved }) {
  const [open, setOpen] = useState(false);
  const [ids, setIds] = useState(user.delivery_agency_ids || []);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/users/${user.id}`, { delivery_agency_ids: ids });
      toast.success(`${user.display_name} · ${ids.length} agencies saved`);
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <>
      <button onClick={() => { setIds(user.delivery_agency_ids || []); setOpen(true); }}
        className="mt-1 flex items-center gap-1 text-[10px] font-mono uppercase text-[#385C4B] hover:underline"
        data-testid={`admin-delivery-agencies-${user.id}`}>
        <Truck className="w-3 h-3" />Agencies ({(user.delivery_agency_ids || []).length})
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-lg w-full max-w-md p-4 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="delivery-agencies-dialog">
            <div className="flex items-center">
              <div className="font-bold">{user.display_name} · Delivery agencies</div>
              <button onClick={() => setOpen(false)} className="ml-auto"><X className="w-4 h-4" /></button>
            </div>
            <DeliveryAgencyPicker value={ids} onChange={setIds} />
            <button onClick={save} disabled={saving} data-testid="delivery-agencies-save"
              className="w-full py-2 rounded-md bg-[#385C4B] text-white text-sm font-semibold disabled:opacity-50">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
